import React, { useState } from 'react';
import Dashboard from './components/Dashboard';
import CustomerTable from './components/CustomerTable';
import AdminPanel from './components/AdminPanel';
import Login from './components/Login';
import ThemeToggle from './components/ThemeToggle';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuthWithRole } from './hooks/useAuthWithRole';
import { ADMIN_CONFIG } from './config/admin';
import { TEXT } from './constants/localization';
import { BarChart3, Table, Settings, LogOut, User } from 'lucide-react';

function App() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'customers' | 'admin'>('dashboard');
  const {
    firebaseUser,
    userRole,
    loading,
    error,
    signOut,
    isAuthenticated,
    hasPermission,
    isAdmin
  } = useAuthWithRole();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        {error ? (
          <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={signOut}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Sign Out
              </button>
            </div>
          </div>
        ) : (
          <Login />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors">
        <nav className="bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{TEXT.APP_TITLE}</h1>
              </div>
              <div className="flex items-center space-x-4">
                <ThemeToggle />
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'dashboard'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart3 className="h-5 w-5 mr-2" />
                  {TEXT.DASHBOARD}
                </button>
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'customers'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Table className="h-5 w-5 mr-2" />
                  {TEXT.CUSTOMERS}
                </button>

                {/* Admin Panel - Only show to admins */}
                {isAdmin() && (
                  <button
                    onClick={() => setActiveTab('admin')}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      activeTab === 'admin'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Settings className="h-5 w-5 mr-2" />
                    Admin
                  </button>
                )}

                {/* User Profile */}
                <div className="flex items-center space-x-3">
                  {firebaseUser?.photoURL && (
                    <img
                      src={firebaseUser.photoURL}
                      alt={firebaseUser.displayName || 'User'}
                      className="h-8 w-8 rounded-full"
                    />
                  )}
                  <div className="hidden sm:block text-sm">
                    <p className="text-gray-900 dark:text-gray-100">
                      {userRole?.displayName || firebaseUser?.displayName}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">
                      {userRole?.email} • {userRole?.role?.replace('_', ' ')}
                    </p>
                  </div>
                  <button
                    onClick={signOut}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="登出"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <main>
          {activeTab === 'dashboard' && <Dashboard />}
          {activeTab === 'customers' && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <CustomerTable />
            </div>
          )}
          {activeTab === 'admin' && <AdminPanel />}
        </main>
      </div>
    </ThemeProvider>
  );
}

export default App;